// A PHOTO ELEMENT'S CROP, DRAWN — the one maths every renderer uses (parity).
//
// A photo element carries `crop` (the window of the photo it shows, 0..1 of
// the full image) and a box. The window is cover-fitted into the box:
//   · the editor and the preview (ThemedPageView, PageElementOverlay) size and
//     offset the <img> so exactly that window shows — `cropCss`;
//   · the print (pdfGenerator drawPageElements) cuts that window out of the
//     bitmap, then the box's aspect out of it, centred — `cropSource`.
// When the window already has the box's aspect (the worker makes it so:
// expand.ts, spread.ts) both show exactly the window. The two halves of an S5
// panorama are two such elements cropped edge to edge: panoramaParity.test.ts
// proves they meet at the spine with no gap and nothing shown twice.
//
// Pure. No DOM.

export interface Crop { x: number; y: number; w: number; h: number }

/**
 * The <img> placement (fractions of the box: left, top, width, height) that
 * shows `crop` of an image of natural size `nat` cover-fitted into a box of
 * aspect `boxAR` (w / h). Multiply by 100 for CSS %.
 */
export function cropCss(crop: Crop, nat: { w: number; h: number }, boxAR: number): { left: number; top: number; width: number; height: number } {
  const winAR = (crop.w * nat.w) / (crop.h * nat.h);
  const wdW = winAR > boxAR ? winAR / boxAR : 1;   // window display size,
  const wdH = winAR > boxAR ? 1 : boxAR / winAR;   // as fraction of the box
  const idW = wdW / crop.w, idH = wdH / crop.h;    // full image display size
  return { left: -(crop.x * idW) - (wdW - 1) / 2, top: -(crop.y * idH) - (wdH - 1) / 2, width: idW, height: idH };
}

/**
 * The bitmap rectangle (px) the print draws into a box of aspect `boxAR`: the
 * crop window of a `bmpW × bmpH` bitmap, then the box's aspect out of it,
 * centred (cover). No crop → the whole bitmap, cover-cut.
 */
export function cropSource(bmpW: number, bmpH: number, crop: Crop | null | undefined, boxAR: number): { sx: number; sy: number; sw: number; sh: number } {
  const clamp01 = (v: number) => Math.min(Math.max(v, 0), 1);
  const winX = crop ? Math.round(clamp01(crop.x) * bmpW) : 0;
  const winY = crop ? Math.round(clamp01(crop.y) * bmpH) : 0;
  const winW = crop ? Math.max(1, Math.min(Math.round(clamp01(crop.w) * bmpW), bmpW - winX)) : bmpW;
  const winH = crop ? Math.max(1, Math.min(Math.round(clamp01(crop.h) * bmpH), bmpH - winY)) : bmpH;
  const srcAR = winW / winH;
  let sw = winW, sh = winH, sx = winX, sy = winY;
  if (srcAR > boxAR) { sw = Math.round(winH * boxAR); sx = winX + Math.round((winW - sw) / 2); }
  else { sh = Math.round(winW / boxAR); sy = winY + Math.round((winH - sh) / 2); }
  return { sx, sy, sw, sh };
}
